"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSelectDate = void 0;
const react_1 = require("react");
const react_i18next_1 = require("react-i18next");
const luxon_1 = require("luxon");
const lodash_1 = require("lodash");
const hooks_1 = require("../../../../../hooks");
const consts_1 = require("../../../../../consts");
const utils_1 = require("../../../../../utils");
const PunchCorrectionStatus_1 = require("../../../../../components/PunchCorrectionStatus");
const context_1 = require("../../../../../components/PunchWorkflow/context");
const BIAnalytics_1 = __importDefault(require("../../../../../utils/BIAnalytics/BIAnalytics"));
const useSelectDate = ({ isWeb }) => {
    const usageLocation = consts_1.featureNames.PUNCH_WORKFLOW_FEATURE;
    const prevSelectedDate = (0, react_1.useRef)();
    const { t } = (0, react_i18next_1.useTranslation)();
    const { featureGates, associateInfo, selectedDateTime, payPeriodBreakdownInfo, setCurrentStepTitle, setIsCurrentStepValid, setSelectedDateTime, setSelectedPunchList, setAction, setSelectedPunchTime, setNewPunchTime, setNewPunchDate, setUseMilitaryTime, setPayPeriodBreakdownInfo, setPunchesForMealLockoutValdn } = (0, context_1.usePunchWorkflowContext)();
    (0, react_1.useEffect)(() => {
        setCurrentStepTitle(t('time_and_attendance_PunchWorkflow_SelectDateStep_Title'));
    }, []);
    const timeRange = {
        start: selectedDateTime.startOf('month').minus({ days: 6 }).toUTC().toISO(),
        end: selectedDateTime.endOf('month').plus({ days: 6 }).toUTC().toISO()
    };
    const { isLoading: isPayPeriodsLoading, data: { pay_period_breakdown_info } = {} } = (0, hooks_1.usePayPeriod)({
        employeeId: associateInfo.employeeId,
        shouldFetch: !payPeriodBreakdownInfo,
        usageLocation
    });
    (0, react_1.useEffect)(() => {
        if (!isPayPeriodsLoading && !payPeriodBreakdownInfo) {
            setPayPeriodBreakdownInfo(pay_period_breakdown_info);
        }
    }, [
        isPayPeriodsLoading,
        payPeriodBreakdownInfo,
        pay_period_breakdown_info,
        setPayPeriodBreakdownInfo
    ]);
    const { isLoading: isScheduleLoading, data: schedule } = (0, hooks_1.useSchedule)({
        employeeId: associateInfo.employeeId,
        timeRange,
        usageLocation
    });
    const { isLoading: isPayCalendarPAPILoading, payCalendar } = (0, hooks_1.usePayCalendarPAPI)({
        employeeId: associateInfo.employeeId,
        timeRange,
        usageLocation
    });
    const searchPunchesResults = (0, hooks_1.useUnifiedPunchesQueries)({
        employeeId: associateInfo.employeeId,
        startTimestamp: timeRange.start,
        endTimestamp: getUnifiedSearchPunchEnd(timeRange.end),
        timezone: associateInfo.timeZone,
        shouldFetch: true,
        usageLocation
    });
    const isSearchPunchesLoading = searchPunchesResults.some((result) => result.isLoading);
    const timespanPunchGroups = searchPunchesResults
        .filter((result) => !!result.data)
        .reduce((accuResult, currResult) => {
        const { timespanPunchGroups } = currResult.data;
        accuResult.push(...(0, lodash_1.cloneDeep)(timespanPunchGroups));
        return accuResult;
    }, []);
    const { isLoading: isPunchCorrStatusListLoading, data: { punchCorrections: punchCorrStatusList = [] } = {} } = (0, hooks_1.usePunchCorrStatusList)({
        enabled: (!isWeb && featureGates.isPunchEditForNativeEnabled) ||
            (isWeb && featureGates.isPunchEditForWebEnabled),
        employeeId: associateInfo.employeeId,
        timeRange: {
            start: luxon_1.DateTime.now().minus({ days: 6 }).startOf('day').toUTC().toISO(),
            end: luxon_1.DateTime.now().endOf('day').toUTC().toISO()
        },
        useV1Api: !!featureGates.isPunchCorrStatusV1Enabled,
        usageLocation
    });
    const dedupedTimespanPunchGroups = dedupeTimespanPunchGroups(timespanPunchGroups);
    const punchesForMealLockoutValdn = (0, utils_1.getPunchesForMealLockoutValdn)(dedupedTimespanPunchGroups, selectedDateTime);
    const searchPunchList = getPunchWithTimespans(dedupedTimespanPunchGroups);
    const filteredPunchWithTimespans = (0, utils_1.findTargetPunchesWithUnifiedPunches)(selectedDateTime, timespanPunchGroups, payPeriodBreakdownInfo);
    const selectedPunchWithTimespans = markInProgressPunch(filteredPunchWithTimespans, punchCorrStatusList);
    const isLoading = isPayPeriodsLoading ||
        isScheduleLoading ||
        isPayCalendarPAPILoading ||
        isSearchPunchesLoading ||
        isPunchCorrStatusListLoading;
    const daysWithShifts = (0, hooks_1.useDaysWithShifts)({ schedule });
    const daysWithPayDays = (0, hooks_1.useDaysWithPayDays)({
        payCalendar,
        futureDateOnly: false
    });
    const daysWithAttention = (0, hooks_1.useDaysWithUnifiedMissedPunch)({
        punchWithTimespans: searchPunchList
    });
    const daysWithPunches = (0, hooks_1.useDaysWithUnifiedPunches)({
        punchWithTimespans: searchPunchList
    });
    (0, react_1.useEffect)(() => {
        if (!isSearchPunchesLoading &&
            !isPunchCorrStatusListLoading &&
            prevSelectedDate.current !== selectedDateTime) {
            prevSelectedDate.current = selectedDateTime;
            setSelectedPunchList((0, lodash_1.cloneDeep)(selectedPunchWithTimespans));
            setPunchesForMealLockoutValdn(punchesForMealLockoutValdn);
        }
    }, [
        isSearchPunchesLoading,
        isPunchCorrStatusListLoading,
        selectedDateTime,
        selectedPunchWithTimespans,
        punchesForMealLockoutValdn,
        setSelectedPunchList,
        setPunchesForMealLockoutValdn
    ]);
    (0, react_1.useEffect)(() => {
        setIsCurrentStepValid(!isLoading && !!selectedDateTime && selectedDateTime <= luxon_1.DateTime.now());
    }, [isLoading, selectedDateTime, setIsCurrentStepValid]);
    const updateDateAction = (date) => {
        BIAnalytics_1.default.selectDateInPW();
        setSelectedDateTime(date);
        setAction(undefined);
        setSelectedPunchTime(undefined);
        setNewPunchTime(undefined);
        setNewPunchDate(undefined);
        setUseMilitaryTime(undefined);
    };
    return {
        isLoading,
        daysWithShifts,
        daysWithPayDays,
        daysWithPunches,
        daysWithAttention,
        punchCorrStatusList,
        updateDateAction
    };
};
exports.useSelectDate = useSelectDate;
const getUnifiedSearchPunchEnd = (timeRangeEnd) => {
    const maxEnd = luxon_1.DateTime.now().plus({ day: 1 });
    const endDateTime = luxon_1.DateTime.fromISO(timeRangeEnd);
    const end = endDateTime > maxEnd ? maxEnd : endDateTime;
    return end.toUTC().toISO();
};
const dedupeTimespanPunchGroups = (timespanPunchGroups) => {
    const groupIds = new Set();
    return timespanPunchGroups.filter((timespanPunchGroup) => {
        if (groupIds.has(timespanPunchGroup.timespanGroupId)) {
            return false;
        }
        groupIds.add(timespanPunchGroup.timespanGroupId);
        return true;
    });
};
const getPunchWithTimespans = (timespanPunchGroups) => {
    const punchWithTimeSpans = timespanPunchGroups.reduce((punchesList, timespanPunchGroup) => {
        punchesList.push(...timespanPunchGroup.punchWithTimespans);
        return punchesList;
    }, []);
    return punchWithTimeSpans;
};
const markInProgressPunch = (punchWithTimespans, punchCorrStatusList) => {
    if (punchCorrStatusList.length === 0) {
        return punchWithTimespans;
    }
    return punchWithTimespans.map((punchWithTimespan) => {
        var _a, _b;
        const temp = Object.assign({}, punchWithTimespan);
        if ((_a = temp.startSpan) === null || _a === void 0 ? void 0 : _a.punchDateTime.timestamp) {
            const punchDateTime = luxon_1.DateTime.fromISO(punchWithTimespan.startSpan.punchDateTime.timestamp);
            temp.startSpan.isInProgress = isPunchInProgress(punchDateTime, punchCorrStatusList);
        }
        if ((_b = temp.endSpan) === null || _b === void 0 ? void 0 : _b.punchDateTime.timestamp) {
            const punchDateTime = luxon_1.DateTime.fromISO(punchWithTimespan.endSpan.punchDateTime.timestamp);
            temp.endSpan.isInProgress = isPunchInProgress(punchDateTime, punchCorrStatusList);
        }
        return temp;
    });
};
const isPunchInProgress = (punchDateTime, punchCorrStatusList) => {
    const index = punchCorrStatusList.findIndex((punchCorrStatus) => luxon_1.DateTime.fromISO(punchCorrStatus.punchDateTime).equals(punchDateTime) &&
        !luxon_1.DateTime.fromISO(punchCorrStatus.newPunchDateTime).equals(punchDateTime) &&
        punchCorrStatus.requestType === PunchCorrectionStatus_1.PunchCorrType.EDIT_PUNCH &&
        (punchCorrStatus.status === PunchCorrectionStatus_1.PunchCorrStatus.PENDING ||
            punchCorrStatus.status === PunchCorrectionStatus_1.PunchCorrStatus.IN_PROGRESS));
    return index > -1;
};
